import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import KeywordOpportunities from "@/components/KeywordOpportunities";
import {
  Loader2,
  Trash2,
  CheckCircle2,
  MousePointerClick,
  Eye,
  Percent,
  TrendingUp,
  Search,
} from "lucide-react";

const REASONS = {
  bad_state: "Your session expired before Google could finish. Please try again.",
  exchange_failed: "Google rejected the connection. Double-check the OAuth client setup.",
  no_refresh_token: "Google didn't return a refresh token. Remove Sapientia from your Google account's third-party access, then reconnect.",
  missing_params: "Google's response was incomplete. Please try again.",
  access_denied: "You declined the permission request.",
};

function fmtInt(v) {
  return (v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function fmtPct(v) {
  return `${((v ?? 0) * 100).toFixed(1)}%`;
}
function fmtPos(v) {
  return (v ?? 0).toFixed(1);
}

function StatCard({ label, value, Icon, color }) {
  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-5">
      <div className="flex items-center justify-between">
        <span className="uppercase-label">{label}</span>
        <Icon className="w-5 h-5" style={{ color: color || "#002FA7" }} strokeWidth={1.75} />
      </div>
      <div className="font-heading text-4xl font-black tracking-tighter mt-2">{value}</div>
    </div>
  );
}

function PerfTable({ title, rows, keyLabel }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100"><h3 className="uppercase-label">{title}</h3></div>
        <div className="p-8 text-center text-gray-500 text-sm">No data for this period.</div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden" data-testid={`gsc-${keyLabel}-table`}>
      <div className="px-5 py-4 border-b border-gray-100"><h3 className="uppercase-label">{title}</h3></div>
      <div className="max-h-[440px] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-5">{keyLabel === "queries" ? "Query" : "Page"}</TableHead>
              <TableHead className="text-right">Clicks</TableHead>
              <TableHead className="text-right">Impr.</TableHead>
              <TableHead className="text-right">CTR</TableHead>
              <TableHead className="text-right pr-5">Pos.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="pl-5 font-medium max-w-[320px] truncate" title={r.key}>{r.key}</TableCell>
                <TableCell className="text-right">{fmtInt(r.clicks)}</TableCell>
                <TableCell className="text-right">{fmtInt(r.impressions)}</TableCell>
                <TableCell className="text-right">{fmtPct(r.ctr)}</TableCell>
                <TableCell className="text-right pr-5">{fmtPos(r.position)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function SearchConsole() {
  const [conns, setConns] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [site, setSite] = useState("");
  const [days, setDays] = useState(28);
  const [perf, setPerf] = useState(null);
  const [loadingPerf, setLoadingPerf] = useState(false);

  const conn = conns && conns[0];

  const load = () =>
    api.get("/gsc/connections").then((r) => setConns(r.data)).catch(() => setConns([]));

  // Handle the redirect back from Google (?gsc=connected|error)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const status = p.get("gsc");
    if (status === "connected") toast.success("Google Search Console connected");
    else if (status === "error") toast.error("Couldn't connect", { description: REASONS[p.get("reason")] || "Please try again." });
    if (status) window.history.replaceState({}, "", "/search-console");
    load();
  }, []);

  // Default the site selector to the first available property.
  useEffect(() => {
    if (conn && conn.sites?.length && !site) setSite(conn.sites[0].site_url);
  }, [conn, site]);

  // Load performance whenever the site or window changes.
  useEffect(() => {
    if (!conn || !site) return;
    setLoadingPerf(true);
    api.get(`/gsc/connections/${conn.id}/performance`, { params: { site, days } })
      .then((r) => setPerf(r.data))
      .catch((e) => {
        setPerf(null);
        toast.error("Could not load data", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
      })
      .finally(() => setLoadingPerf(false));
  }, [conn?.id, site, days]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/gsc/oauth/start");
      window.location.href = data.authorization_url;  // full-page redirect to Google
    } catch (e) {
      setConnecting(false);
      toast.error("Could not start Google sign-in", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const disconnect = async () => {
    await api.delete(`/gsc/connections/${conn.id}`);
    setPerf(null);
    setSite("");
    load();
    toast.success("Disconnected");
  };

  if (conns === null)
    return <div className="flex justify-center pt-20"><Loader2 className="w-8 h-8 animate-spin text-klein" /></div>;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Search Console</h1>
        <p className="text-gray-500 mt-2">Real Google rank &amp; traffic data — clicks, impressions, CTR and average position.</p>
      </div>

      {!conn ? (
        <div className="max-w-xl">
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-klein/10 flex items-center justify-center mx-auto">
              <TrendingUp className="w-6 h-6 text-klein" />
            </div>
            <h3 className="font-heading text-xl font-bold tracking-tight mt-4">Connect Google Search Console</h3>
            <p className="text-gray-500 mt-1 max-w-md mx-auto text-sm">
              Sign in with the Google account that owns your Search Console property to pull first-party
              rank and traffic data — more accurate than any third-party estimate.
            </p>
            <Button onClick={connect} disabled={connecting} data-testid="gsc-connect-button" className="mt-5 bg-klein hover:bg-blue-800 text-white px-6 py-6">
              {connecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</> : "Connect with Google"}
            </Button>
          </div>
          <div className="mt-4 bg-gray-50 border-l-4 border-l-klein rounded-r-lg p-4 text-sm text-gray-600">
            You'll be sent to Google to approve read-only access to your Search Console data. Sapientia never sees your Google password.
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="gsc-connection-card">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-good/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-good" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-gray-900">Google Search Console</div>
                <div className="text-sm text-gray-500">{conn.sites?.length || 0} propert{(conn.sites?.length || 0) === 1 ? "y" : "ies"} available</div>
              </div>
            </div>
            <Button variant="outline" onClick={disconnect} data-testid="gsc-disconnect-button" className="text-bad border-gray-300 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-2" /> Disconnect
            </Button>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={site}
              onChange={(e) => setSite(e.target.value)}
              data-testid="gsc-site-select"
              className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium min-w-[240px] max-w-full"
            >
              {(conn.sites || []).map((s) => (
                <option key={s.site_url} value={s.site_url}>{s.site_url}</option>
              ))}
            </select>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="gsc-range-select"
              className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium"
            >
              <option value={7}>Last 7 days</option>
              <option value={28}>Last 28 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            {loadingPerf && <Loader2 className="w-5 h-5 animate-spin text-klein" />}
          </div>

          {(conn.sites || []).length === 0 && (
            <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-8 text-center text-gray-500 text-sm">
              This Google account has no Search Console properties with data access.
            </div>
          )}

          {perf && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Clicks" value={fmtInt(perf.totals.clicks)} Icon={MousePointerClick} />
                <StatCard label="Impressions" value={fmtInt(perf.totals.impressions)} Icon={Eye} />
                <StatCard label="Avg. CTR" value={fmtPct(perf.totals.ctr)} Icon={Percent} />
                <StatCard label="Avg. Position" value={fmtPos(perf.totals.position)} Icon={TrendingUp} />
              </div>
              <div className="text-xs text-gray-400">{perf.start_date} → {perf.end_date}</div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PerfTable title="Top Queries" rows={perf.queries} keyLabel="queries" />
                <PerfTable title="Top Pages" rows={perf.pages} keyLabel="pages" />
              </div>
              <KeywordOpportunities connId={conn.id} site={site} days={days} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
